import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { IAutocompleteResponse, IGeocodeResponse, IGeocodeResponseV2, IPlaceDetailsResponse, IRouteResponse } from '@core/interface';
import { environment } from '@core/envs';
import { IPlaceDetailsV2Response } from '../../interfaces/map/google/place-details-v2-response.interface';

type googleSearch = 'latlng' | 'address';

@Injectable({
  providedIn: 'root'
})
export class GoogleMapsApiService {
  readonly #gatewayUrl = environment.getRoute('google', false, true);
  readonly #http = inject(HttpClient);

  geocodeBy(type: googleSearch, search: string): Observable<IGeocodeResponse> {
    const url = `${this.#gatewayUrl}/maps/maps/api/geocode/json`;
    const headers = {
      'X-Gtw-Params': `${type}=${encodeURIComponent(search)}`
    };

    return this.#http.get<IGeocodeResponse>(url, { headers });
  }

  /**
   * Versão V2 do geocodeBy: mesma URL/endpoint, porém o backend agora retorna também o objeto
   * "address" estruturado (addressLine1/2/3/4, city, state, stateCode, postalCode), evitando a
   * necessidade de parsear manualmente o address_components "cru" do Google (fonte de bugs de
   * state/stateCode incorretos). Mantido em paralelo ao geocodeBy original para não quebrar
   * outros consumidores que ainda dependem do formato antigo.
   */
  geocodeByV2(type: googleSearch, search: string): Observable<IGeocodeResponseV2> {
    const url = `${this.#gatewayUrl}/maps/maps/api/geocode/json`;
    const headers = {
      'X-Gtw-Params': `${type}=${encodeURIComponent(search)}`
    };

    return this.#http.get<IGeocodeResponseV2>(url, { headers });
  }

  autocompletePlaces(input: string, regionCode: string, languageCode: string): Observable<IAutocompleteResponse> {
    const url = `${this.#gatewayUrl}/places/v1/places:autocomplete`;
    const headers = {
      'X-Goog-FieldMask': 'suggestions.placePrediction.text.text,suggestions.placePrediction.placeId'
    };

    const body = {
      input,
      languageCode,
      includedRegionCodes: [regionCode] // Código de pais ISO 3166-1 alfa-2
    };
    return this.#http.post<IAutocompleteResponse>(url, body, { headers });
  }

  placeDetails(placeId: string): Observable<IPlaceDetailsResponse> {
    const url = `${this.#gatewayUrl}/places/v1/places`;
    const headers = {
      'X-Gtw-Params': `placeid=${encodeURIComponent(placeId)}`,
      'X-Goog-FieldMask': '*'
    };
    return this.#http.get<IPlaceDetailsResponse>(url, { headers });
  }

  placeDetailsV2(placeId: string): Observable<IPlaceDetailsV2Response> {
    const url = `${this.#gatewayUrl}/places/v2/places/?fields=id,displayName,formattedAddress,addressComponents,location`;
    const headers = {
      'X-Gtw-Params': `placeid=${encodeURIComponent(placeId)}`,
      'X-Goog-FieldMask': '*'
    };
    return this.#http.get<IPlaceDetailsV2Response>(url, { headers });
  }

  getDistanceBetweenPoints(origin: { lat: number; lng: number }, destination: { lat: number; lng: number }): Observable<IRouteResponse> {
    const url = `${this.#gatewayUrl}/routes/directions/v2:computeRoutes`;

    const headers = {
      'X-Goog-FieldMask': 'routes.distanceMeters,routes.duration',
    };

    const body = {
      origin: {
        location: {
          latLng: {
            latitude: origin?.lat,
            longitude: origin?.lng
          }
        }
      },
      destination: {
        location: {
          latLng: {
            latitude: destination?.lat,
            longitude: destination?.lng
          }
        }
      },
      travelMode: 'DRIVE',
      requestedReferenceRoutes: ["SHORTER_DISTANCE"]
    };

    return this.#http.post<IRouteResponse>(url, body, { headers }).pipe(
      map(data => {
        const routes = data?.routes ?? [];

        return {
          routes: routes.map(route => ({
            ...route,
            distanceMeters: Number((route.distanceMeters / 1000).toFixed(1)), // Convertendo de metros para quilômetros
          }))
        };
      })
    );
  }
}
